import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  Chip,
  Card,
  IconButton,
  Collapse,
  Modal,
  CircularProgress,
} from "@mui/material";
import { MdEdit, MdOutlineListAlt } from "react-icons/md";
import AddIcon from "@mui/icons-material/Add";
import guestGroupApi from "../../../api/guestGroupApi";
import AddGuestRequestForm from "./AddGuestRequestForm";
import DeleteGuestRequest from "./DeleteGuestRequest";
import DualMembers from "./DualMembers";
import EditGuestRequestForm from "./EditGuestRequestForm";
import EditGuestGroupForm from "./EditGuestGroupForm";
import enumMapper from "../../../enumMapper";

export default function TripGroupPage() {
  const [groups, setGroups] = useState([]);
  const [selectedGroupInfo, setSelectedGroupInfo] = useState(null);
  const [selectedReq, setSelectedReq] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const [loading, setLoading] = useState(true); // start loading as true
  const [expandedGroupIds, setExpandedGroupIds] = useState([]);
  const [openAddGuestRequestModal, setOpenAddGuestRequestModal] = useState(false);
  const [openUpdateGuestRequestModal, setOpenUpdateGuestRequestModal] = useState(false);
  const [openUpdateGuestGroupModal, setOpenUpdateGuestGroupModal] = useState(false);

  // Fetch groups
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await guestGroupApi.GetGroupInfo();
        if (response.data.data) {
          const dataArray = Array.isArray(response.data.data)
            ? response.data.data
            : [response.data.data];
          setGroups(dataArray);
        } else {
          setGroups([]);
        }
      } catch (error) {
        console.error("Failed to fetch data", error);
        setGroups([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [refresh]);

  // Refresh function
  const RefreshGrid = (result) => {
    if (result) setRefresh((old) => !old);
    handleCloseAddGuestRequestModal();
    handleCloseUpdateGuestRequestModal();
    handleCloseUpdateGuestGroupModal();
  };

  // Collapse toggle
  const toggleGroup = (groupId) => {
    setExpandedGroupIds((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId]
    );
  };

  // Modals
  const handleOpenAddGuestRequestModal = (group) => {
    setSelectedGroupInfo(group);
    setOpenAddGuestRequestModal(true);
  };
  const handleCloseAddGuestRequestModal = () => {
    setSelectedGroupInfo(null);
    setOpenAddGuestRequestModal(false);
  };

  const handleOpenUpdateGuestGroupModal = (group) => {
    setSelectedGroupInfo(group);
    setOpenUpdateGuestGroupModal(true);
  };
  const handleCloseUpdateGuestGroupModal = () => {
    setSelectedGroupInfo(null);
    setOpenUpdateGuestGroupModal(false);
  };

  const handleOpenUpdateGuestRequestModal = (row) => {
    setSelectedReq(row);
    setOpenUpdateGuestRequestModal(true);
  };
  const handleCloseUpdateGuestRequestModal = () => {
    setSelectedReq(null);
    setOpenUpdateGuestRequestModal(false);
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: 350, sm: 450 },
    bgcolor: "background.paper",
    borderRadius: 3,
    boxShadow: 24,
    p: 4,
  };

  return (
    <Box p={3}>
      {/* Page Title */}
      <Typography
        variant="h5"
        fontWeight="bold"
        mb={3}
        display="flex"
        alignItems="center"
        gap={1}
      >
        <MdOutlineListAlt /> درخواست های من
      </Typography>

      {/* Loading / No Data / Group List */}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={5}>
          <CircularProgress />
        </Box>
      ) : groups.length === 0 ? (
        <Typography sx={{ fontStyle: "italic" }}>
          هیچ گروهی موجود نیست.
        </Typography>
      ) : (
        <Stack spacing={3}>
          {groups.map((group) => {
            const parent = group.allMembers.find((m) => m.isParent);
            const isExpanded = expandedGroupIds.includes(group.id);
            return (
              <Card
                key={group.id}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  boxShadow: 3,
                  transition: "0.3s",
                  "&:hover": { boxShadow: 6 },
                }}
              >
                {/* Group Header */}
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                  mb={1}
                >
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      سرپرست گروه: {parent.name} {parent.family}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {group.persian_StartTrip} تا {group.persian_EndTrip}
                    </Typography>
                     <Typography variant="body2" color="text.secondary">
                      {group.description}
                    </Typography>
                    <IconButton
                      onClick={() => handleOpenUpdateGuestGroupModal(group)}
                    >
                      <MdEdit />
                    </IconButton>
                  </Box>
                  <Stack
                    direction="row"
                    spacing={1}
                    mt={{ xs: 1, md: 0 }}
                    alignItems="center"
                  >
                    <Chip
                      label={group.guestGroupStepTitle}
                      color={
                        group.guestGroupStepTitle === "تایید شده"
                          ? "success"
                          : "warning"
                      }
                    />
                    <Button variant="text" onClick={() => toggleGroup(group.id)}>
                      {isExpanded ? "بستن جزئیات گروه" : "جزئیات گروه"}
                    </Button>
                  </Stack>
                </Stack>
                {/* Collapsible Requests */}
                <Collapse in={isExpanded}>
                  <Stack spacing={2} mt={2}>
                    <Box display="flex" justifyContent="flex-end">
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenAddGuestRequestModal(group)}
                      >
                        افزودن درخواست
                      </Button>
                    </Box>
                    {group.requests.length === 0 && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontStyle: "italic" }}
                      >
                        هیچ درخواستی ثبت نشده است.
                      </Typography>
                    )}
                    {group.requests.map((req) => (
                      <Card
                        key={req.id}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          boxShadow: 1,
                          backgroundColor: "#fdfdfd",
                          transition: "0.3s",
                          "&:hover": { boxShadow: 3 },
                        }}
                      >
                        <Stack
                          direction={{ xs: "column", md: "row" }}
                          justifyContent="space-between"
                          alignItems={{ xs: "flex-start", md: "flex-start" }}
                          mb={1}
                          spacing={1}
                        >
                          <Stack direction="column" spacing={0.5}>
                            <Typography fontWeight="bold">
                              {req.service.guestServicesTitle} -{" "}
                              {req.service.cityConsistencyTitle} (
                              {req.service.guestServiceTypeTitle})
                            </Typography>
                            <Typography color="text.secondary" variant="body2">
                              {req.persian_From} - {req.persian_To}
                            </Typography>
                            {req.originStation && <Typography color="text.secondary" variant="body2">
                              {req.originStationTitle} ←{" "} {req.destinationStationTitle}
                            </Typography>
                            }
                          </Stack>
                          {req.service.guestServices !== enumMapper.GuestServices.Visa && <Stack direction="row" spacing={1}>
                            <IconButton
                              onClick={() => handleOpenUpdateGuestRequestModal(req)}
                            >
                              <MdEdit />
                            </IconButton>
                            <DeleteGuestRequest
                              id={req.id}
                              refreshGrid={RefreshGrid}
                            />
                          </Stack>
                          }
                        </Stack>
                        <Box mt={1}>
                          <DualMembers
                            requestId={req.id}
                            groupInfo={group}
                            requestMembers={req.requestMembers}
                            refreshGrid={RefreshGrid}
                          />
                        </Box>
                      </Card>
                    ))}
                  </Stack>
                </Collapse>
              </Card>
            );
          })}
        </Stack>
      )}

      {/* Add Request Modal */}
      <Modal open={openAddGuestRequestModal} onClose={handleCloseAddGuestRequestModal}>
        <Box sx={modalStyle}>
          <Typography variant="h6" mb={2}>
            افزودن درخواست
          </Typography>
          {selectedGroupInfo && (
            <AddGuestRequestForm
              groupInfo={selectedGroupInfo}
              refreshGrid={RefreshGrid}
            />
          )}
        </Box>
      </Modal>

      {/* Update Request Modal */}
      <Modal
        keepMounted
        open={openUpdateGuestRequestModal}
        onClose={handleCloseUpdateGuestRequestModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={modalStyle}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            ویرایش درخواست
          </Typography>
          <Box sx={{ mt: 4 }}>
            {selectedReq && (
              <EditGuestRequestForm row={selectedReq} refreshGrid={RefreshGrid} />
            )}
          </Box>
        </Box>
      </Modal>

      {/* Update GuestGroup Modal */}
      <Modal
        keepMounted
        open={openUpdateGuestGroupModal}
        onClose={handleCloseUpdateGuestGroupModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={modalStyle}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            ویرایش
          </Typography>
          <Box sx={{ mt: 4 }}>
            {selectedGroupInfo && (
              <EditGuestGroupForm row={selectedGroupInfo} refreshGrid={RefreshGrid} />
            )}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}